import * as React from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext.js";
import { apiFetch } from "@/lib/api.js";
import { cn } from "@/lib/utils.js";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

// ─── Types ───────────────────────────────────────────────────────────

interface Household {
  id: string; name: string; plan: string; planOverride: string | null;
  ownerEmail: string | null; memberCount: number; createdAt: string;
}

interface Overview {
  totalUsers: number; totalHouseholds: number; totalItems: number; totalSpaces: number;
  onboardingCompleted: number; dau: number; wau: number; mau: number;
}

interface GrowthData {
  signups: { date: string; count: number }[];
  retention: { cohort_week: string; cohort_size: number; week1: number; week2: number; week4: number }[];
}

interface FeatureRow { feature: string; total: number; uniqueUsers: number; uniqueHouseholds: number; }
interface FeatureDaily { date: string; feature: string; count: number; }
interface FeatureData { byFeature: FeatureRow[]; daily: FeatureDaily[]; adoption: Record<string, number>; }

interface AIData {
  totalCost: number; totalTokens: number; totalCalls: number;
  byFeature: { feature: string; cost: number; calls: number; tokens: number }[];
  byModel: { model: string; cost: number; calls: number }[];
  daily: { date: string; cost: number; calls: number }[];
  limitUsage: Record<string, number>;
}

interface RevenueData {
  planDistribution: { plan: string; count: number }[];
  mrr: number; totalHouseholds: number; paidHouseholds: number; conversionRate: number;
  recentChanges: { id: string; householdId: string; oldPlan: string; newPlan: string; trigger: string; createdAt: string }[];
}

interface ContentData {
  totalItems: number; withPhoto: number; withSpace: number; stale90d: number;
  totalSpaces: number; emptySpaces: number; maxDepth: number;
  itemsPerHousehold: { median: number; avg: number; max: number; distribution: number[] };
}

type Tab = "overview" | "households" | "growth" | "features" | "ai" | "revenue" | "content";

const TABS: { key: Tab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "growth", label: "Growth" },
  { key: "features", label: "Features" },
  { key: "ai", label: "AI Costs" },
  { key: "revenue", label: "Revenue" },
  { key: "content", label: "Content" },
  { key: "households", label: "Households" },
];

// Warm Order chart palette — amethyst signal + sage + the domestic status set,
// no Bootstrap indigo/red/green (the app's charts speak the same vocabulary).
const COLORS = ["#5A4A78", "#6E8C6A", "#C9862E", "#C0562F", "#3E3251", "#4C6B4A", "#12874A"];

// ─── Main Component ──────────────────────────────────────────────────

export default function AdminPage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [tab, setTab] = React.useState<Tab>("overview");
  const [loading, setLoading] = React.useState(true);

  // Data states
  const [households, setHouseholds] = React.useState<Household[]>([]);
  const [overview, setOverview] = React.useState<Overview | null>(null);
  const [growth, setGrowth] = React.useState<GrowthData | null>(null);
  const [features, setFeatures] = React.useState<FeatureData | null>(null);
  const [ai, setAI] = React.useState<AIData | null>(null);
  const [revenue, setRevenue] = React.useState<RevenueData | null>(null);
  const [content, setContent] = React.useState<ContentData | null>(null);

  React.useEffect(() => {
    if (!user?.isAdmin) { navigate("/"); return; }
    loadTab(tab);
  }, [user]);

  React.useEffect(() => { loadTab(tab); }, [tab]);

  async function loadTab(t: Tab) {
    setLoading(true);
    try {
      switch (t) {
        case "overview": if (!overview) setOverview(await apiFetch("/api/admin/stats/overview")); break;
        case "households": if (!households.length) setHouseholds(await apiFetch("/api/admin/households")); break;
        case "growth": if (!growth) setGrowth(await apiFetch("/api/admin/stats/growth")); break;
        case "features": if (!features) setFeatures(await apiFetch("/api/admin/stats/features")); break;
        case "ai": if (!ai) setAI(await apiFetch("/api/admin/stats/ai")); break;
        case "revenue": if (!revenue) setRevenue(await apiFetch("/api/admin/stats/revenue")); break;
        case "content": if (!content) setContent(await apiFetch("/api/admin/stats/content")); break;
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function updateOverride(id: string, value: string) {
    const planOverride = value === "" ? null : value;
    await apiFetch(`/api/admin/households/${id}/plan`, { method: "PATCH", body: JSON.stringify({ planOverride }) });
    setHouseholds(await apiFetch("/api/admin/households"));
  }

  const spinner = <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-pill animate-spin" /></div>;

  return (
    <div>
      <h1 className="text-2xl font-semibold font-headline text-amethyst-ink tracking-[-0.01em] mb-4">Admin Dashboard</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto border-b border-clay-border pb-px">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={cn("px-3 py-2 text-sm font-medium rounded-t-md whitespace-nowrap transition-colors",
              tab === t.key ? "bg-card text-primary border-b-2 border-primary" : "text-faint hover:text-body"
            )}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? spinner : (
        <>
          {tab === "overview" && overview && <OverviewTab data={overview} />}
          {tab === "growth" && growth && <GrowthTab data={growth} />}
          {tab === "features" && features && <FeaturesTab data={features} />}
          {tab === "ai" && ai && <AITab data={ai} />}
          {tab === "revenue" && revenue && <RevenueTab data={revenue} />}
          {tab === "content" && content && <ContentTab data={content} />}
          {tab === "households" && <HouseholdsTab households={households} onOverride={updateOverride} />}
        </>
      )}
    </div>
  );
}

// ─── Shared Components ───────────────────────────────────────────────

function Card({ title, children, className }: { title?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-lg border border-clay-border bg-card p-4", className)}>
      {title && <h3 className="text-sm font-medium text-faint mb-3">{title}</h3>}
      {children}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div>
      <p className="text-2xl font-semibold text-ink tabular-nums">{typeof value === "number" ? value.toLocaleString() : value}</p>
      <p className="text-sm text-body">{label}</p>
      {sub && <p className="text-[0.8125rem] text-faint mt-0.5">{sub}</p>}
    </div>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const colors: Record<string, string> = {
    pro: "bg-sage-wash text-sage-deep", plus: "bg-amethyst-soft text-amethyst-ink", free: "bg-clay-muted text-faint",
  };
  return <span className={cn("px-2 py-0.5 rounded-pill text-[0.8125rem] font-medium capitalize", colors[plan] ?? colors.free)}>{plan}</span>;
}

function pct(n: number, d: number): string {
  return d > 0 ? `${Math.round((n / d) * 100)}%` : "—";
}

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ─── Overview Tab ────────────────────────────────────────────────────

function OverviewTab({ data }: { data: Overview }) {
  const stickiness = data.mau > 0 ? Math.round((data.dau / data.mau) * 100) : 0;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><Stat label="Total Users" value={data.totalUsers} /></Card>
        <Card><Stat label="Households" value={data.totalHouseholds} /></Card>
        <Card><Stat label="Total Items" value={data.totalItems} /></Card>
        <Card><Stat label="Total Spaces" value={data.totalSpaces} /></Card>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><Stat label="DAU" value={data.dau} /></Card>
        <Card><Stat label="WAU" value={data.wau} /></Card>
        <Card><Stat label="MAU" value={data.mau} /></Card>
        <Card><Stat label="Stickiness (DAU/MAU)" value={`${stickiness}%`} sub={stickiness > 25 ? "Strong" : stickiness > 10 ? "Moderate" : "Low"} /></Card>
      </div>
      <Card title="Onboarding">
        <div className="flex items-center gap-4">
          <Stat label="Completed Onboarding" value={data.onboardingCompleted} />
          <Stat label="Completion Rate" value={pct(data.onboardingCompleted, data.totalUsers)} />
        </div>
      </Card>
    </div>
  );
}

// ─── Growth Tab ──────────────────────────────────────────────────────

function GrowthTab({ data }: { data: GrowthData }) {
  const chartData = data.signups.map((s) => ({ date: fmtDate(s.date), signups: s.count }));
  return (
    <div className="space-y-6">
      <Card title="Daily Signups (last 90 days)">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="signups" fill="var(--color-amethyst)" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      {data.retention.length > 0 && (
        <Card title="Weekly Retention Cohorts">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-clay-border text-left text-body">
                  <th className="px-3 py-2 font-medium">Cohort Week</th>
                  <th className="px-3 py-2 font-medium">Size</th>
                  <th className="px-3 py-2 font-medium">Week 1</th>
                  <th className="px-3 py-2 font-medium">Week 2</th>
                  <th className="px-3 py-2 font-medium">Week 4</th>
                </tr>
              </thead>
              <tbody>
                {data.retention.map((r) => (
                  <tr key={r.cohort_week} className="border-b border-clay-border/60">
                    <td className="px-3 py-2">{fmtDate(r.cohort_week)}</td>
                    <td className="px-3 py-2">{r.cohort_size}</td>
                    <td className="px-3 py-2">{pct(r.week1, r.cohort_size)}</td>
                    <td className="px-3 py-2">{pct(r.week2, r.cohort_size)}</td>
                    <td className="px-3 py-2">{pct(r.week4, r.cohort_size)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Features Tab ────────────────────────────────────────────────────

function FeaturesTab({ data }: { data: FeatureData }) {
  const chartData = data.byFeature.map((f) => ({ name: f.feature.replace(/_/g, " "), uses: f.total, users: f.uniqueUsers }));
  const totalHouseholds = data.adoption.total_households ?? 1;
  const adoptionRows = [
    { feature: "Items", pct: pct(data.adoption.households_items ?? 0, totalHouseholds) },
    { feature: "AI", pct: pct(data.adoption.households_ai ?? 0, totalHouseholds) },
    { feature: "Workflows", pct: pct(data.adoption.households_flows ?? 0, totalHouseholds) },
    { feature: "Search", pct: pct(data.adoption.households_search ?? 0, totalHouseholds) },
    { feature: "CSV Export", pct: pct(data.adoption.households_export ?? 0, totalHouseholds) },
    { feature: "Declutter", pct: pct(data.adoption.households_declutter ?? 0, totalHouseholds) },
  ];

  return (
    <div className="space-y-6">
      <Card title="Feature Usage (last 30 days)">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
              <Tooltip />
              <Bar dataKey="uses" fill="var(--color-amethyst)" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card title="Feature Adoption (% of households)">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {adoptionRows.map((r) => (
            <div key={r.feature} className="flex justify-between items-center p-2 rounded-lg bg-paper-raised">
              <span className="text-sm">{r.feature}</span>
              <span className="text-sm font-semibold">{r.pct}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── AI Tab ──────────────────────────────────────────────────────────

function AITab({ data }: { data: AIData }) {
  const dailyChart = data.daily.map((d) => ({ date: fmtDate(d.date), cost: Number(d.cost.toFixed(4)), calls: d.calls }));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card><Stat label="Total Cost (30d)" value={`$${data.totalCost.toFixed(2)}`} /></Card>
        <Card><Stat label="Total Calls" value={data.totalCalls} /></Card>
        <Card><Stat label="Total Tokens" value={data.totalTokens.toLocaleString()} /></Card>
      </div>
      <Card title="Daily AI Cost">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyChart}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v: number) => `$${v.toFixed(4)}`} />
              <Line type="monotone" dataKey="cost" stroke="var(--color-amethyst)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Cost by Feature">
          <table className="w-full text-sm">
            <tbody>
              {data.byFeature.map((f) => (
                <tr key={f.feature} className="border-b border-clay-border/60">
                  <td className="py-1.5">{f.feature}</td>
                  <td className="py-1.5 text-right font-mono">${f.cost.toFixed(4)}</td>
                  <td className="py-1.5 text-right text-faint">{f.calls} calls</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card title="Cost by Model">
          <table className="w-full text-sm">
            <tbody>
              {data.byModel.map((m) => (
                <tr key={m.model} className="border-b border-clay-border/60">
                  <td className="py-1.5">{m.model}</td>
                  <td className="py-1.5 text-right font-mono">${m.cost.toFixed(4)}</td>
                  <td className="py-1.5 text-right text-faint">{m.calls} calls</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
      <Card title="AI Limit Utilization">
        <div className="flex gap-6 text-sm">
          <div>Free at limit: <span className="font-semibold">{data.limitUsage.free_at_limit ?? 0}</span></div>
          <div>Plus at limit: <span className="font-semibold">{data.limitUsage.plus_at_limit ?? 0}</span></div>
          <div>Total households: <span className="font-semibold">{data.limitUsage.total_households ?? 0}</span></div>
        </div>
      </Card>
    </div>
  );
}

// ─── Revenue Tab ─────────────────────────────────────────────────────

function RevenueTab({ data }: { data: RevenueData }) {
  const pieData = data.planDistribution.map((p) => ({ name: p.plan, value: p.count }));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><Stat label="MRR" value={`$${data.mrr.toFixed(2)}`} /></Card>
        <Card><Stat label="Paid Households" value={data.paidHouseholds} /></Card>
        <Card><Stat label="Conversion Rate" value={`${data.conversionRate}%`} /></Card>
        <Card><Stat label="Total Households" value={data.totalHouseholds} /></Card>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Plan Distribution">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Recent Plan Changes">
          <div className="max-h-56 overflow-y-auto">
            {data.recentChanges.length === 0 ? <p className="text-faint text-sm">No plan changes yet.</p> : (
              <table className="w-full text-sm">
                <tbody>
                  {data.recentChanges.slice(0, 20).map((c) => (
                    <tr key={c.id} className="border-b border-clay-border/60">
                      <td className="py-1.5"><PlanBadge plan={c.oldPlan} /> → <PlanBadge plan={c.newPlan} /></td>
                      <td className="py-1.5 text-faint text-xs">{c.trigger}</td>
                      <td className="py-1.5 text-faint text-xs">{fmtDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── Content Tab ─────────────────────────────────────────────────────

function ContentTab({ data }: { data: ContentData }) {
  const total = data.totalItems || 1;
  const quality = [
    { label: "Has Photo", value: data.withPhoto, pct: pct(data.withPhoto, total) },
    { label: "Has Space", value: data.withSpace, pct: pct(data.withSpace, total) },
    { label: "Stale (90d+)", value: data.stale90d, pct: pct(data.stale90d, total) },
  ];

  // Bucket the distribution for a histogram
  const dist = data.itemsPerHousehold.distribution;
  const buckets = [
    { label: "0", count: 0 }, { label: "1-5", count: 0 }, { label: "6-20", count: 0 },
    { label: "21-50", count: 0 }, { label: "51-100", count: 0 }, { label: "100+", count: 0 },
  ];
  for (const c of dist) {
    if (c === 0) buckets[0].count++;
    else if (c <= 5) buckets[1].count++;
    else if (c <= 20) buckets[2].count++;
    else if (c <= 50) buckets[3].count++;
    else if (c <= 100) buckets[4].count++;
    else buckets[5].count++;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><Stat label="Total Items" value={data.totalItems} /></Card>
        <Card><Stat label="Total Spaces" value={data.totalSpaces} /></Card>
        <Card><Stat label="Empty Spaces" value={data.emptySpaces} /></Card>
        <Card><Stat label="Max Space Depth" value={data.maxDepth} /></Card>
      </div>
      <Card title="Data Quality">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quality.map((q) => (
            <div key={q.label} className="p-3 rounded-lg bg-paper-raised">
              <p className="text-lg font-semibold text-ink tabular-nums">{q.pct}</p>
              <p className="text-xs text-body">{q.label} ({q.value})</p>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Items per Household">
        <div className="flex gap-6 mb-3 text-sm">
          <div>Median: <span className="font-semibold">{data.itemsPerHousehold.median}</span></div>
          <div>Average: <span className="font-semibold">{data.itemsPerHousehold.avg}</span></div>
          <div>Max: <span className="font-semibold">{data.itemsPerHousehold.max}</span></div>
        </div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={buckets}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="var(--color-sage)" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

// ─── Households Tab (original) ───────────────────────────────────────

function HouseholdsTab({ households, onOverride }: { households: Household[]; onOverride: (id: string, v: string) => void }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-clay-border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-clay-border text-left text-body">
            <th className="px-4 py-3 font-medium">Household</th>
            <th className="px-4 py-3 font-medium">Owner</th>
            <th className="px-4 py-3 font-medium">Stripe Plan</th>
            <th className="px-4 py-3 font-medium">Override</th>
            <th className="px-4 py-3 font-medium">Effective</th>
            <th className="px-4 py-3 font-medium">Members</th>
            <th className="px-4 py-3 font-medium">Created</th>
          </tr>
        </thead>
        <tbody>
          {households.map((h) => {
            const effective = h.planOverride ?? h.plan;
            return (
              <tr key={h.id} className="border-b border-clay-border/60 hover:bg-paper-raised/60">
                <td className="px-4 py-3 font-medium">{h.name}</td>
                <td className="px-4 py-3 text-body">{h.ownerEmail ?? "—"}</td>
                <td className="px-4 py-3"><PlanBadge plan={h.plan} /></td>
                <td className="px-4 py-3">
                  <select value={h.planOverride ?? ""} onChange={(e) => onOverride(h.id, e.target.value)}
                    className="border border-clay-border rounded-lg px-2 py-1 text-sm bg-card">
                    <option value="">None</option>
                    <option value="free">Free</option>
                    <option value="plus">Plus</option>
                    <option value="pro">Pro</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  <PlanBadge plan={effective} />
                  <span className={cn("ml-1.5 text-[0.8125rem] font-medium uppercase tracking-wide", h.planOverride ? "text-warning-text" : "text-faint")}>
                    {h.planOverride ? "override" : "stripe"}
                  </span>
                </td>
                <td className="px-4 py-3 text-body">{h.memberCount}</td>
                <td className="px-4 py-3 text-faint">{new Date(h.createdAt).toLocaleDateString()}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {households.length === 0 && <p className="text-center text-faint py-8">No households found.</p>}
    </div>
  );
}
